'use client'

import { cn, isActivePathname, titleCaseToCamelCase } from '@cloud/shared/ui'
import type { DialogProps } from '@radix-ui/react-dialog'
import { useLocation, useRouter } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Search } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { DynamicIcon } from '@/components/dynamic-icon'
import { groupHostsModule } from '@/components/integrations/dataset-menu'
import { useDatasetMenu } from '@/components/integrations/use-dataset-menu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { DialogTitle } from '@/components/ui/dialog'
import { Keyboard } from '@/components/ui/keyboard'
import { moduleNavigationsData } from '@/data/module-navigations'
import { settingsNavigationData } from '@/data/settings-navigation'
import { HelpCommandResults } from '@/help/components/help-command-results'
import { useHiddenSidebarModules } from '@/hooks/use-hidden-sidebar-modules'
import { useModulesContext } from '@/providers/modules-provider'
import type { NavigationNestedItem, NavigationRootItem } from '@/types'

interface CommandMenuProps extends DialogProps {
  buttonClassName?: string
}

export function CommandMenu({ buttonClassName, ...props }: CommandMenuProps) {
  const [open, setOpen] = useState(false)
  // Gestuurd, zodat de handleiding met dezelfde zoekvraag kan zoeken (`HelpCommandResults`).
  const [query, setQuery] = useState('')
  const { pathname } = useLocation()
  const router = useRouter()
  const t = useTranslations()
  const { isModuleEnabled, isFeatureEnabled } = useModulesContext()
  const { isHidden } = useHiddenSidebarModules()
  const datasets = useDatasetMenu()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        if (
          (e.target instanceof HTMLElement && e.target.isContentEditable) ||
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return
        }

        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const runCommand = useCallback((command: () => unknown) => {
    setOpen(false)
    command()
  }, [])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  // Flatten the nested moduleNavigationsData tree into a list of
  // searchable leaves. Previous implementation tried to render nested
  // <Collapsible> inside <CommandItem>, which (a) broke cmdk's keyword
  // filtering because triggers swallowed the search, and (b) rendered
  // the list into an *empty* <ScrollArea>, so there was nothing to
  // search against in the first place.
  //
  // Now: walk the tree, drop the nesting, keep one CommandItem per
  // leaf with a deep label ("HR › Leave") so the search can hit
  // either the group name or the child name. Module-gated items are
  // filtered out when their module is disabled — matches the sidebar.
  type Leaf = {
    key: string
    title: string
    groupTitle: string
    pathKey: string // full breadcrumb, used as the display label
    href: string
    iconName?: NavigationRootItem['iconName']
    isActive: boolean
  }

  const shouldShow = (item: NavigationRootItem | NavigationNestedItem): boolean => {
    if (!('moduleKey' in item) || !item.moduleKey) return true
    if (!isModuleEnabled(item.moduleKey)) return false
    return !item.featureKey || isFeatureEnabled(item.moduleKey, item.featureKey)
  }

  const collectLeaves = (
    items: (NavigationRootItem | NavigationNestedItem)[],
    groupTitle: string,
    breadcrumb: string[],
  ): Leaf[] => {
    const out: Leaf[] = []
    for (const item of items) {
      if (!shouldShow(item)) continue
      const translated = t(`navigation.${titleCaseToCamelCase(item.title)}`)
      const trail = [...breadcrumb, translated]
      if (item.items && item.items.length > 0) {
        out.push(...collectLeaves(item.items, groupTitle, trail))
      } else if ('href' in item && item.href) {
        out.push({
          key: `${groupTitle}:${item.href}`,
          title: translated,
          groupTitle,
          pathKey: trail.join(' › '),
          href: item.href,
          iconName: 'iconName' in item ? item.iconName : undefined,
          isActive: isActivePathname(item.href, pathname),
        })
      }
    }
    return out
  }

  // Een dataset met een menuplek hoort bij de groep van zijn module, net als in de zijbalk; zijn
  // titel is al vertaald.
  const datasetLeaves = (
    items: (NavigationRootItem | NavigationNestedItem)[],
    groupTitle: string,
  ) =>
    datasets
      .filter((d) => groupHostsModule(items, d.moduleKey) && isModuleEnabled(d.moduleKey))
      .map(
        (d): Leaf => ({
          key: `dataset:${d.taskId}`,
          title: d.title,
          groupTitle,
          // Zoals `collectLeaves` voor een item op het eerste niveau: alleen de titel.
          pathKey: d.title,
          href: d.href,
          iconName: d.iconName,
          isActive: isActivePathname(d.href, pathname),
        }),
      )

  const groupedLeaves = moduleNavigationsData
    .map((nav) => ({
      title: t(`navigation.${titleCaseToCamelCase(nav.title)}`),
      leaves: [...collectLeaves(nav.items, nav.title, []), ...datasetLeaves(nav.items, nav.title)],
    }))
    .filter((g) => g.leaves.length > 0)

  // The settings area navigates through its own shell (SettingsNav), so its
  // pages are indexed from settings-navigation.ts to stay searchable here.
  const settingsTitle = t('navigation.settings')
  for (const group of settingsNavigationData) {
    if (group.moduleKey && (!isModuleEnabled(group.moduleKey) || isHidden(group.moduleKey)))
      continue
    const groupTitle = t(group.titleKey)
    groupedLeaves.push({
      title: `${settingsTitle} › ${groupTitle}`,
      leaves: group.items
        .filter(
          (item) =>
            !item.featureKey ||
            (group.moduleKey && isFeatureEnabled(group.moduleKey, item.featureKey)),
        )
        .map((item) => ({
          key: `settings:${item.href}`,
          title: t(item.titleKey),
          groupTitle,
          pathKey: `${settingsTitle} › ${groupTitle} › ${t(item.titleKey)}`,
          href: item.href,
          iconName: undefined,
          isActive: isActivePathname(item.href, pathname),
        })),
    })
  }

  return (
    <>
      <Button
        variant="outline"
        size="lg"
        className={cn(
          'max-w-64 w-full justify-start px-3 rounded-md bg-muted/50 text-muted-foreground',
          buttonClassName,
        )}
        onClick={() => setOpen(true)}
        {...props}
      >
        <Search className="me-2 h-4 w-4" />
        <span>{t('search.search')}</span>
        <Keyboard className="ms-auto">K</Keyboard>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen} {...props}>
        <DialogTitle className="sr-only">Search Menu</DialogTitle>
        <CommandInput
          placeholder={t('search.typeCommand')}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList className="max-h-[400px]">
          <CommandEmpty>{t('search.noResults')}</CommandEmpty>
          {groupedLeaves.map((group) => (
            <CommandGroup key={group.title} heading={group.title}>
              {group.leaves.map((leaf) => (
                <CommandItem
                  key={leaf.key}
                  // `value` is what cmdk matches on; use the full
                  // breadcrumb so e.g. typing "hr leave" finds
                  // "HR › Leave".
                  value={leaf.pathKey}
                  onSelect={() => runCommand(() => router.navigate({ to: leaf.href }))}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5',
                    leaf.isActive && 'bg-accent',
                  )}
                >
                  {leaf.iconName ? (
                    <DynamicIcon name={leaf.iconName} className="h-4 w-4" />
                  ) : (
                    <DynamicIcon name="Circle" className="h-4 w-4" />
                  )}
                  <span>{leaf.title}</span>
                  <Badge variant="secondary" className="ml-auto text-[10px]">
                    {group.title}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          <HelpCommandResults
            query={query}
            onPick={(hit) =>
              runCommand(() =>
                router.navigate({
                  to: '/help/$',
                  params: { _splat: hit.id },
                  hash: hit.anchor || undefined,
                }),
              )
            }
          />
        </CommandList>
      </CommandDialog>
    </>
  )
}
